/**
 * shared/types/settings.ts — Settings: occasions and holidays, name caps, member profile, couple settings, and the SettingsAPI slice.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
/* ---- 4c. Settings structure (card yQtaPZmm4vzI + IKK45inZ4vKE) ----
 *
 * Individual half lives on the member (a birthday is one person's), joint half
 * on the couple (an anniversary and the gift-lead times are shared). The
 * holiday list is what the gifting agent will read in a later card — this
 * card only stores and serves it.
 */

/** The fixed occasions — always present, always in this order. */
export const HOLIDAY_KEYS = [
  'birthday', 'anniversary', 'fathers-day', 'mothers-day', 'valentines',
] as const;

export type FixedHolidayKey = (typeof HOLIDAY_KEYS)[number];

/**
 * Custom occasions are numbered `custom-1`..`custom-N` by the DO in list
 * order, so the client never has to mint a unique key. `custom` on its own is
 * still ACCEPTED on input (it is what stored rows and older clients carry) and
 * is renumbered on the way through.
 */
export type HolidayKey = FixedHolidayKey | `custom-${number}`;
export const CUSTOM_KEY_RE = /^custom(-\d+)?$/;
export const MAX_CUSTOM_OCCASIONS = 5;
/** Custom occasion dates are month-and-day only: the occasion repeats yearly. */
export const MONTH_DAY_RE = /^\d{2}-\d{2}$/;

export interface HolidaySetting {
  key: HolidayKey;
  /** Empty until a custom occasion is named. */
  label: string;
  enabled: boolean;
  /** How far ahead the agent should start asking about gifts. */
  leadDays: number;
  /** Custom occasions only: MM-DD, repeats every year. Null until set. */
  date?: string | null;
}

/** Longest a real name or a chat display name may be (card yFLCjm7ONIEc).
 *  Both live in text columns with no CHECK, so the length rule is enforced on
 *  the write path — one number the worker, the DO and the Settings inputs all
 *  read, so the server's reason and the client's maxLength never disagree. */
export const NAME_MAX = 60;
export const DISPLAY_NAME_MAX = 60;

export interface MemberProfile {
  memberId: string;
  /** YYYY-MM-DD, or null when never set. */
  birthday: string | null;
  /** The chat name (members.display_name) — what the partner sees. Null only
   *  when the member row does not exist. */
  displayName: string | null;
  /** The reply-text colour key (members.reply_colour), null until picked. */
  replyColour: string | null;
}

/**
 * What the agent does with an inbox message that turns into a to-do:
 * `suggest` posts it for the pair to accept (the concierge default),
 * `auto` files it as an accepted to-do straight away. Off is not a mode here —
 * the `todos` AI permission is that switch.
 */
export type InboxTodoMode = 'suggest' | 'auto';
export const INBOX_TODO_MODES = ['suggest', 'auto'] as const;

export interface CoupleSettings {
  /** YYYY-MM-DD, or null when never set. */
  anniversary: string | null;
  holidays: HolidaySetting[];
  inboxTodoMode: InboxTodoMode;
  /** Task Assist (card to follow, spec kZX3gsJkKaTH): one couple-level
   *  consent switch for the chore-balance engine, plus whether the one-time
   *  offer still has to be shown. One row, so the two partners can never
   *  disagree about whether it is on. */
  taskAssist: TaskAssist;
}

export interface TaskAssist {
  /** Has the couple turned it on? Off until someone says yes — the engine
   *  reads this before it may surface anything. */
  enabled: boolean;
  /** Should the rail show the offer card now? True only while nobody has
   *  answered AND the couple has at least one completed chore to read. Once
   *  shown (or answered) it never comes back; Settings is the way in after. */
  offer: boolean;
}

export interface SettingsAPI {
  getMemberProfile(memberId: string): Promise<MemberProfile>;
  setMemberBirthday(memberId: string, birthday: string | null): Promise<MemberProfile>;
  /** The chat name (card yFLCjm7ONIEc). Writes members.display_name and tells
   *  both sockets the members list changed, so the partner sees it live. */
  setMemberDisplayName(memberId: string, displayName: string): Promise<MemberProfile>;
  /** Reply-text colour (card 9QADJv3CdiOh). Writes members.reply_colour and
   *  broadcasts 'members', same live path as the chat name. Throws
   *  'bad_reply_colour' for a key outside REPLY_COLOUR_KEYS. */
  setMemberReplyColour(memberId: string, colourKey: string): Promise<MemberProfile>;
  getCoupleSettings(): Promise<CoupleSettings>;
  setCoupleSettings(
    patch: {
      anniversary?: string | null;
      holidays?: HolidaySetting[];
      inboxTodoMode?: InboxTodoMode;
      /** Task Assist consent (spec kZX3gsJkKaTH). */
      taskAssist?: boolean;
      /** 'shown' stamps the one-time offer as delivered, 'no' means the
       *  couple declined it — both are permanent, neither re-asks. */
      taskAssistOffer?: 'shown' | 'no';
    },
    /** Who answered, for `enabled_by`. Optional so older callers still work. */
    memberId?: string | null,
  ): Promise<CoupleSettings>;
}

/* ---- Hints (card 9tynEjL4q4DT) ----
 *
 * The first-move `.tab-hint` one-liners are dismissed ONCE PER COUPLE: the
 * seen-flags live in the couple room's `hints_seen` table (schema in
 * durable-object/couple-settings.ts), not in any one device's localStorage,
 * so a hint dismissed on the phone is not drawn on the laptop either. The
 * key union is defined HERE because both the worker (route validation) and
 * the web (the store) read it — one definition, no duplicate.
 */
export const HINT_KEYS = ['thread', 'life', 'notebook'] as const;
export type HintKey = (typeof HINT_KEYS)[number];
export function isHintKey(value: unknown): value is HintKey {
  return typeof value === 'string' && (HINT_KEYS as readonly string[]).includes(value);
}

/** Statements against the room's own hints_seen table. A Durable Object's
 *  storage IS the couple's, so hint_key alone is the key — no query can name
 *  another couple (see COUPLE_INVARIANTS in room.ts). The statements live with
 *  the contract so the settings slice and the /api/hints route cannot drift. */
export const HINTS_SEEN_SELECT_SQL = 'SELECT hint_key FROM hints_seen ORDER BY seen_at, hint_key';
export const HINTS_SEEN_INSERT_SQL =
  'INSERT INTO hints_seen (hint_key, seen_at) VALUES (?, ?) ON CONFLICT(hint_key) DO NOTHING';
